import { Injectable } from '@angular/core';


import { Observable, of, merge, combineLatest } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap, take } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { MultiBaseAbstractApi } from '@api/genericApi/multi-base-abstract.api';
import { MediaSubscriptionApiService } from '@api/mediaSubscriptions/media-subscription.api.service';
import { MediaSubscriptionApi } from '@api/mediaSubscriptions/models/media-subscription.api.model';
import { SubscriptionContentTimeslot } from '@api/mediaSubscriptions/models/subscription-content-timeslot.api.model';


import { trimLongStringWithEllipsis } from '@Core/utils';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';


import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { AllResourceAbstractService } from '@Common/Basic/service/all-resource-abstract.service';
import { ResourceActionTypes } from '@Common/Basic/enums/resource-actions.enum';
import { ResourceAction } from '@Common/Basic/service/resource-abstract.service';

import { MediaSubscription } from '@Monitor/Shared/models/media-subscription.model';
import { MediaSubscriptionStatusEnum } from '@Monitor/Shared/enums/media-subscription-status.enum';

@Injectable()
export class AgentService extends AllResourceAbstractService<MediaSubscription, MediaSubscriptionApi, MediaSubscription, MediaSubscriptionApi> {

    all$: Observable<MediaSubscription[]> = this.userStateService.hasSocialAccess().pipe(
        switchMap(hasPermission =>
            hasPermission ? merge(
                this.getApiService().getList().pipe(
                    map(list => list
                        .map(item => this.constructResourceModel(item))
                        .sort((a, b) => a.name.localeCompare(b.name))
                    ),
                ),
                this.refreshAllManualSubject
            ) : of([])
        ),
        map((agents: MediaSubscription[]) => agents.filter(agent => agent.isAgent)),
        shareReplay(1)
    );

    constructor(
        private mediaSubscriptionApiService: MediaSubscriptionApiService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        private userStateService: CurrentUserStateService,
    ) {
        super();
    }

    getApiService(): MultiBaseAbstractApi<MediaSubscriptionApi, MediaSubscriptionApi> {
        return this.mediaSubscriptionApiService;
    }

    constructListModel(apiModel: MediaSubscriptionApi): MediaSubscription {
        return new MediaSubscription(apiModel);
    }

    constructResourceModel(apiModel: MediaSubscriptionApi): MediaSubscription {
        return new MediaSubscription(apiModel);
    }

    refreshAllManual(): Observable<MediaSubscription[]> {
        return this.getApiService().getList().pipe(
            map(list => list
                .map(item => this.constructResourceModel(item))
                .sort((a, b) => a.name.localeCompare(b.name))
            ),
            tap((list) => this.refreshAllManualSubject.next(list)),
            take(1)
        );
    }


    create(agent: MediaSubscription): Observable<number> {
        return this.createResource(agent).pipe(
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    map(x => id)
                );
            })
        );
    }

    createCopy(agent: MediaSubscription): Observable<number> {
        return this.createResource(agent, { actionType: ResourceActionTypes.COPY }).pipe(
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    map(x => id)
                );
            })
        );
    }

    update(agent: MediaSubscription, broadcast: boolean = true): Observable<number> {
        return this.updateResource(agent, { actionType: ResourceActionTypes.UPDATE }, broadcast).pipe(
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    map(x => id)
                );
            })
        );
    }

    delete(agent: MediaSubscription): Observable<number> {
        return this.deleteResource(agent.id, {
            actionType: ResourceActionTypes.DELETE,
            resource: agent
        }).pipe(
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    map(x => id)
                );
            }),
        );
    }

    switchChange(
        status: MediaSubscriptionStatusEnum,
        agent: MediaSubscription,
        message: boolean = true
    ): Observable<number> {
        const clonedAgent = agent.clone();
        clonedAgent.status = status;

        return this.update(clonedAgent, message);
    }

    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as MediaSubscription;

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Social.agentWidget.create.error.heading',
                        detail: this.localizationService.transform('Social.agentWidget.create.error.message', {
                            name: trimLongStringWithEllipsis(resource.name, 38)
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Social.agentWidget.create.success.heading',
                        detail: this.localizationService.transform('Social.agentWidget.create.success.message', {
                            name: trimLongStringWithEllipsis(resource.name, 38)
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.UPDATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Social.manageTopicsDialog.update.error.heading',
                        detail: this.localizationService.transform('Social.manageTopicsDialog.update.error.message', {
                            name: trimLongStringWithEllipsis(resource.name, 32)
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Social.manageTopicsDialog.update.success.heading',
                        detail: this.localizationService.transform('Social.manageTopicsDialog.update.success.message', {
                            name: trimLongStringWithEllipsis(resource.name, 32)
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Social.manageTopicsDialog.delete.error.heading',
                        detail: this.localizationService.transform('Social.manageTopicsDialog.delete.error.message', {
                            name: trimLongStringWithEllipsis(resource.name, 32)
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Social.manageTopicsDialog.delete.success.heading',
                        detail: this.localizationService.transform('Social.manageTopicsDialog.delete.success.message', {
                            name: trimLongStringWithEllipsis(resource.name, 32)
                        })
                    });
                }
                break;
            }
            case ResourceActionTypes.COPY: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Social.agentWidget.saveAs.error.heading',
                        detail: this.localizationService.transform('Social.agentWidget.saveAs.error.message', {
                            name: trimLongStringWithEllipsis(resource.name, 32)
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Social.agentWidget.saveAs.success.heading',
                        detail: this.localizationService.transform('Social.agentWidget.saveAs.success.message', {
                            name: trimLongStringWithEllipsis(resource.name, 32)
                        })
                    });
                }
                break;
            }
        }
    }

    /**
     * Gets the N top subscriptions based on Quota Usage
     * @param count Number of subscriptions to get
     */
    getTopSubscriptions(count: number): Observable<{subscription: MediaSubscription; count: number}[]> {
        return combineLatest([
            this.all$,
            this.mediaSubscriptionApiService.getTopSubscriptions(count, true)
        ]).pipe(
            take(1),
            map(([agents, values]) => {
                return values
                    .map(value => {
                        return {
                            subscription: agents.find((sub) => +sub.id === +value.value),
                            count: value.count
                        };
                    })
                    // Filter out ids that we couldn't find subscriptions for
                    .filter(combo => !!combo.subscription);
            })
        );
    }

    /**
     * Gets the quota usage over time data for the account
     */
    getUsageOverTimeSummary(): Observable<SubscriptionContentTimeslot[]> {
        return this.mediaSubscriptionApiService.getContentOverTime(null, true);
    }
}
